import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { API_BASE } from "../config";
import { useSearchParams, useParams, Link } from "react-router-dom";
import {
  RiFileTextLine,
  RiArrowRightLine,
  RiRefreshLine,
  RiAddLine,
  RiPresentationLine,
  RiCheckLine,
  RiPhoneLine,
  RiMailLine,
  RiPrinterLine,
} from "react-icons/ri";
import toast from "react-hot-toast";
import { useSummit } from "./summitdetails";

/* ─── THEME COLORS ──────────────────────────────────────────────────────── */
const T = {
  white: "#FFFFFF",
  offWhite: "#F4F9F8",
  softTeal: "#E8F5F3",
  primary: "#2A9D8F",
  primaryDark: "#1F7A6E",
  primaryLight: "#3BBCAE",
  navy: "#1A3A4A",
  navyDeep: "#0D2B3E",
  accent: "#3BBCAE",
  accentGold: "#D4A017",
  accentGoldDark: "#B8880F",
  accentGoldLight: "#E8B520",
  text: "#1C3461",
  textMid: "#2D4A5A",
  textMuted: "#6B8090",
  border: "#C8E8E4",
  borderLight: "#E2F2F0",
  shadow: "0 4px 24px rgba(42,157,143,0.10)",
  shadowMd: "0 8px 40px rgba(42,157,143,0.14)",
  shadowLg: "0 20px 60px rgba(13,43,62,0.20)",
  shadowGold: "0 8px 32px rgba(212,160,23,0.25)",
  gradPrimary: "linear-gradient(135deg, #2A9D8F 0%, #3BBCAE 100%)",
  gradNavy: "linear-gradient(135deg, #0D2B3E 0%, #1A3A4A 100%)",
  gradSoft: "linear-gradient(135deg, #E8F5F3 0%, #F4F9F8 100%)",
  gradGold: "linear-gradient(135deg, #D4A017 0%, #E8B520 100%)",
  gradGoldReverse: "linear-gradient(135deg, #E8B520 0%, #D4A017 100%)",
};

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800;900&family=Inter:wght@300;400;500;600;700;800;900&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  html{scroll-behavior:smooth;-webkit-font-smoothing:antialiased}
  body,#root{background:${T.offWhite};color:${T.text};font-family:'Inter',sans-serif}
  ::-webkit-scrollbar{width:8px}
  ::-webkit-scrollbar-track{background:${T.borderLight}}
  ::-webkit-scrollbar-thumb{background:${T.primaryLight};border-radius:4px}

  .t-hero{background:${T.gradSoft};border-bottom:1px solid ${T.border};padding:clamp(48px,7vw,80px) clamp(20px,4vw,64px) clamp(40px,5vw,64px);position:relative;overflow:hidden}
  .t-hero::before{content:'';position:absolute;top:-120px;left:50%;transform:translateX(-50%);width:600px;height:600px;background:radial-gradient(circle,rgba(42,157,143,0.08) 0%,transparent 70%);pointer-events:none}
  .t-hero-i{max-width:1320px;margin:0 auto;display:flex;align-items:flex-start;justify-content:space-between;gap:40px;flex-wrap:wrap;position:relative}
  .t-hero-lbl{display:inline-flex;align-items:center;gap:8px;padding:5px 14px;border-radius:20px;background:${T.softTeal};border:1px solid ${T.border};color:${T.primary};font-size:11px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;margin-bottom:20px}
  .t-hero-h{font-family:'Playfair Display',serif;font-size:clamp(2rem,4.5vw,3.4rem);font-weight:900;color:${T.navy};letter-spacing:-.03em;line-height:1.06;margin-bottom:14px}
  .t-hero-h em{font-style:normal;color:${T.accentGold}}
  .t-hero-p{font-size:clamp(.88rem,1.3vw,1rem);color:${T.textMid};line-height:1.7;max-width:500px}
  .t-stats{display:flex;gap:1px;background:${T.border};border-radius:14px;border:1px solid ${T.border};overflow:hidden;align-self:flex-start;flex-shrink:0}
  .t-st{background:${T.white};padding:20px 28px;display:flex;flex-direction:column;gap:4px;min-width:100px}
  .t-st:nth-child(2){background:${T.offWhite}}
  .t-st-n{font-family:'Playfair Display',serif;font-size:1.8rem;font-weight:900;color:${T.accentGold};line-height:1;letter-spacing:-.03em}
  .t-st-l{font-size:11px;color:${T.textMuted};font-weight:500;text-transform:uppercase;letter-spacing:.08em}

  .t-wrapper{padding:40px clamp(20px,4vw,64px) 56px;background:${T.offWhite}}
  .t-container{max-width:1320px;margin:0 auto}

  /* GRID LAYOUT */
  .t-layout{display:grid;grid-template-columns:280px 1fr;gap:32px;align-items:start}
  @media(max-width:900px){.t-layout{grid-template-columns:1fr}}

  /* SIDEBAR */
  .t-sidebar{background:${T.white};border-radius:20px;border:1px solid ${T.borderLight};padding:24px;box-shadow:${T.shadow};position:sticky;top:24px}
  .t-sidebar-title{font-size:16px;font-weight:700;color:${T.navy};margin-bottom:16px;display:flex;align-items:center;gap:8px}
  .t-nav-item{width:100%;text-align:left;padding:10px 14px;border-radius:10px;font-size:13px;color:${T.textMid};transition:all 0.2s;cursor:pointer;display:flex;align-items:center;gap:10px;background:none;border:none;font-family:'Inter',sans-serif}
  .t-nav-item:hover,.t-nav-item.active{background:${T.softTeal};color:${T.primary};font-weight:500}

  /* GUIDELINE CARDS */
  .t-card{background:${T.white};border-radius:20px;border:1px solid ${T.borderLight};overflow:hidden;box-shadow:${T.shadow};transition:all 0.3s ease;margin-bottom:20px;cursor:pointer}
  .t-card:hover{box-shadow:${T.shadowMd};border-color:${T.primaryLight};transform:translateY(-2px)}
  .t-card-header{display:flex;align-items:flex-start;gap:16px;padding:24px}
  .t-card-icon{width:56px;height:56px;border-radius:16px;background:${T.softTeal};display:flex;align-items:center;justify-content:center;font-size:26px;flex-shrink:0;transition:transform 0.3s}
  .t-card:hover .t-card-icon{transform:scale(1.08)}
  .t-card-order{font-size:11px;padding:3px 10px;border-radius:20px;background:${T.softTeal};color:${T.primary};display:inline-block;margin-bottom:6px}
  .t-card-title{font-size:17px;font-weight:700;color:${T.text};margin-bottom:6px;line-height:1.3}
  .t-card-desc{font-size:14px;color:${T.textMuted};line-height:1.65}
  .t-card-body{padding:0 24px 24px 96px}
  .t-expand-icon{color:${T.primaryLight};flex-shrink:0;transition:transform 0.3s;font-size:20px}

  /* SECTION HEADER */
  .t-section-header{text-align:center;margin-bottom:40px}
  .t-section-title{font-family:'Playfair Display',serif;font-size:clamp(1.6rem,2.5vw,2.2rem);font-weight:800;color:${T.navy};margin-bottom:12px}
  .t-divider{width:64px;height:3px;background:${T.gradPrimary};border-radius:2px;margin:0 auto 16px}

  /* GRID VIEW */
  .t-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:20px}
  @media(max-width:1100px){.t-grid{grid-template-columns:repeat(2,1fr)}}
  @media(max-width:640px){.t-grid{grid-template-columns:1fr}}
  .t-grid-card{background:${T.white};border-radius:16px;border:1px solid ${T.borderLight};overflow:hidden;box-shadow:${T.shadow};transition:all 0.3s ease;display:flex;flex-direction:column}
  .t-grid-card:hover{transform:translateY(-6px);box-shadow:${T.shadowLg};border-color:${T.primaryLight}}
  .t-grid-header{background:linear-gradient(135deg,${T.softTeal} 0%,${T.white} 100%);padding:24px 24px 16px;border-bottom:1px solid ${T.borderLight}}
  .t-grid-icon{width:60px;height:60px;border-radius:18px;background:${T.white};border:1px solid ${T.borderLight};display:flex;align-items:center;justify-content:center;font-size:28px;margin-bottom:12px;box-shadow:${T.shadow}}
  .t-grid-body{padding:16px 24px 24px;flex:1}

  /* CTA */
  .t-cta-wrap{padding:0 clamp(20px,4vw,64px) 56px}
  .t-cta{max-width:1320px;margin:0 auto;background:${T.gradNavy};border-radius:20px;padding:clamp(32px,4vw,52px) clamp(32px,4vw,60px);display:flex;align-items:center;justify-content:space-between;gap:32px;flex-wrap:wrap;position:relative;overflow:hidden}
  .t-cta::before{content:'';position:absolute;inset:0;background:radial-gradient(circle at 50% 100%,rgba(212,160,23,0.12) 0%,transparent 60%);pointer-events:none}
  .t-cta-tag{display:inline-block;padding:4px 12px;border-radius:20px;background:rgba(212,160,23,0.15);border:1px solid rgba(212,160,23,0.25);color:${T.accentGoldLight};font-size:11px;letter-spacing:.08em;text-transform:uppercase;margin-bottom:12px;font-weight:700}
  .t-cta-h{font-family:'Playfair Display',serif;font-size:clamp(1.4rem,2.5vw,2rem);font-weight:900;color:${T.white};line-height:1.2}
  .t-cta-p{font-size:14px;color:rgba(255,255,255,0.6);margin-top:8px}
  .t-cta-btn{display:inline-flex;align-items:center;gap:8px;padding:13px 28px;background:${T.gradGold};color:${T.navyDeep};border-radius:50px;text-decoration:none;font-size:14px;font-weight:700;transition:all .2s;border:none;cursor:pointer}
  .t-cta-btn:hover{transform:translateY(-2px);box-shadow:${T.shadowGold}}
  .t-cta-btn-outline{display:inline-flex;align-items:center;gap:8px;padding:13px 28px;background:transparent;border:1px solid rgba(255,255,255,0.3);color:${T.white};border-radius:50px;text-decoration:none;font-size:14px;font-weight:700;transition:all .2s}
  .t-cta-btn-outline:hover{background:rgba(255,255,255,0.1);transform:translateY(-2px)}

  /* CONTACT CARD */
  .t-contact{background:${T.gradPrimary};border-radius:20px;padding:32px;text-align:center;margin-top:32px}

  /* ACTION BUTTONS */
  .t-actions{display:flex;flex-wrap:wrap;justify-content:center;gap:12px;padding:16px 0 32px}
  .t-act-btn{display:inline-flex;align-items:center;gap:8px;padding:10px 24px;border-radius:40px;font-size:14px;font-weight:600;cursor:pointer;transition:all 0.2s;background:${T.white};color:${T.primary};border:1px solid ${T.border}}
  .t-act-btn:hover{transform:scale(1.05);box-shadow:${T.shadowMd};border-color:${T.primary}}

  /* EMPTY / ERROR STATES */
  .t-empty{max-width:1320px;margin:0 auto;display:flex;flex-direction:column;align-items:center;text-align:center;padding:80px 24px;gap:12px}
  .t-empty-icon{width:64px;height:64px;border-radius:16px;background:${T.softTeal};border:1px solid ${T.border};display:flex;align-items:center;justify-content:center;font-size:28px;color:${T.primary};margin-bottom:8px}
  .t-empty-title{font-family:'Playfair Display',serif;font-size:22px;font-weight:800;color:${T.navy}}
  .t-empty-sub{font-size:14px;color:${T.textMuted};max-width:360px;line-height:1.6}
  .t-empty-btn{margin-top:8px;padding:10px 22px;border-radius:8px;border:1px solid ${T.border};background:${T.white};color:${T.text};font-family:'Inter',sans-serif;font-size:13px;font-weight:500;cursor:pointer;transition:all .15s;text-decoration:none;display:inline-block}
  .t-empty-btn:hover{border-color:${T.primary};color:${T.primary}}

  /* COPY BUTTON */
  .t-copy{font-size:12px;padding:6px 16px;border-radius:20px;background:${T.offWhite};color:${T.primary};border:none;cursor:pointer;transition:all 0.2s;margin-top:8px}
  .t-copy:hover{background:${T.softTeal}}

  @keyframes spin{to{transform:rotate(360deg)}}
  @keyframes fadeUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
`;

/* ════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ════════════════════════════════════════════════════════════════════ */
const PresentationGuidelinesPage = () => {
  // Get eventId from both query params AND URL params
  const [searchParams] = useSearchParams();
  const { eventId: paramEventId } = useParams();
  const summitContext = useSummit();
  const eventId = summitContext?.eventId || searchParams.get("eventId") || paramEventId;

  const [guidelines, setGuidelines] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [viewMode, setViewMode] = useState("list");
  
  // ✅ State for contact info
  const [contactInfo, setContactInfo] = useState({
    email: "presenters@meetings21.com",
    phone: "+1 (555) 123-4567"
  });

  // ✅ Fetch contact info with eventId
  useEffect(() => {
    if (eventId) {
      fetchContactInfo(eventId);
    } else {
      // If no eventId, fetch without filter (global)
      fetchContactInfo();
    }
  }, [eventId]);

  // ✅ Fetch contact info from API
  const fetchContactInfo = async (eventIdParam) => {
    try {
      let url = `${API_BASE}/api/contacts/public/info`;
      if (eventIdParam) {
        url += `?eventId=${eventIdParam}`;
      }
      const { data } = await axios.get(url);
      if (data.success && data.data && data.data.length > 0) {
        const contact = data.data[0];
        setContactInfo({
          email: contact.email || "presenters@meetings21.com",
          phone: contact.phone || "+1 (555) 123-4567"
        });
        console.log("✅ Contact info loaded for event:", eventIdParam || "global", contact);
      } else {
        console.log("ℹ️ No contacts found, using defaults");
      }
    } catch (error) {
      console.error("❌ Error fetching contact info:", error);
      // Keep default values if API fails
    }
  };

  useEffect(() => {
    console.log("🔍 PresentationGuidelinesPage - eventId:", eventId);
    fetchGuidelines();
  }, [eventId]);

  const fetchGuidelines = async () => {
    if (!eventId) {
      setError("No event selected. Please go back and select an event to view guidelines.");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      console.log(`📡 Fetching guidelines for event: ${eventId}`);

      const { data } = await axios.get(
        `${API_BASE}/api/presentation-guidelines/active/${eventId}`,
        { headers: { "Cache-Control": "no-cache" } }
      );

      console.log("✅ Presentation guidelines response:", data);

      if (data.success && data.data) {
        setGuidelines(data.data);
      } else {
        setError(data.message || "No active presentation guidelines found for this event.");
      }
    } catch (err) {
      console.error("❌ Error fetching presentation guidelines:", err);
      if (err.response?.status === 404) {
        setError("No presentation guidelines have been added for this event yet.");
      } else if (err.response?.status === 400) {
        setError("Invalid event. Please go back and try again.");
      } else {
        setError(err.response?.data?.message || "Failed to load presentation guidelines.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    const printContent = `
      <!DOCTYPE html><html><head>
        <title>${guidelines?.title || "Presentation Guidelines"}</title>
        <meta charset="UTF-8">
        <style>
          *{margin:0;padding:0;box-sizing:border-box}
          body{font-family:'Segoe UI',sans-serif;max-width:900px;margin:0 auto;padding:40px;color:#1C3461;line-height:1.6}
          h1{color:#2A9D8F;border-bottom:3px solid #2A9D8F;padding-bottom:12px;margin-bottom:20px;font-size:28px}
          .desc{font-size:15px;color:#2D4A5A;margin-bottom:30px}
          .item{margin-bottom:24px;padding:20px;background:#E8F5F3;border-radius:12px;border-left:4px solid #2A9D8F;page-break-inside:avoid}
          .item-icon{font-size:28px;margin-bottom:10px}
          .item-title{font-size:17px;font-weight:700;color:#1A3A4A;margin-bottom:8px}
          .item-desc{font-size:14px;color:#2D4A5A;line-height:1.6}
          .contact{margin-top:40px;padding:20px;background:#2A9D8F;color:white;border-radius:12px;text-align:center}
          .footer{margin-top:40px;padding-top:16px;border-top:1px solid #C8E8E4;text-align:center;color:#6B8090;font-size:12px}
          @media print{.item{break-inside:avoid}}
        </style>
      </head><body>
        <h1>${guidelines?.title || "Presentation Guidelines"}</h1>
        <p class="desc">${guidelines?.description || ""}</p>
        ${(guidelines?.guidelines || [])
          .sort((a, b) => a.order - b.order)
          .map(item => `
            <div class="item">
              <div class="item-icon">${item.icon || "📌"}</div>
              <div class="item-title">${item.title}</div>
              <div class="item-desc">${item.description}</div>
            </div>
          `).join("")}
        <div class="contact">
          <h3>Need Help?</h3>
          <p>Email: ${contactInfo.email}</p>
          <p>Phone: ${contactInfo.phone}</p>
        </div>
        <div class="footer"><p>© ${new Date().getFullYear()} Meeting21. All Rights Reserved.</p></div>
      </body></html>
    `;
    const win = window.open("", "_blank");
    win.document.write(printContent);
    win.document.close();
    win.print();
    toast.success("Print window opened!");
  };

  // ── Loading ──────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ background: T.offWhite, minHeight: "100vh" }}>
        <style>{css}</style>
        <div className="t-hero">
          <div className="t-hero-i">
            <div>
              <div className="t-hero-lbl"><RiPresentationLine size={13} />Meeting21 · Presenter Information</div>
              <h1 className="t-hero-h">Presentation <em>Guidelines</em></h1>
              <p className="t-hero-p">Loading guidelines...</p>
            </div>
          </div>
        </div>
        <div style={{ textAlign: "center", padding: "80px 20px" }}>
          <div style={{ width: 48, height: 48, border: `3px solid ${T.border}`, borderTopColor: T.primary, borderRadius: "50%", animation: "spin 1s linear infinite", margin: "0 auto 16px" }} />
          <p style={{ color: T.textMuted, fontSize: 15 }}>Loading presentation guidelines...</p>
        </div>
      </div>
    );
  }

  // ── Error / No Data ──────────────────────────────────────────────
  if (error || !guidelines) {
    return (
      <div style={{ background: T.offWhite, minHeight: "100vh" }}>
        <style>{css}</style>
        <div className="t-hero">
          <div className="t-hero-i">
            <div>
              <div className="t-hero-lbl"><RiPresentationLine size={13} />Meeting21 · Presenter Information</div>
              <h1 className="t-hero-h">Presentation <em>Guidelines</em></h1>
              <p className="t-hero-p">Essential information for all presenters at Meeting21</p>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 24 }}>
                <div style={{ flex: 1, height: 2, background: `linear-gradient(90deg, transparent, ${T.primary}40, transparent)` }} />
                <div style={{ width: 8, height: 8, background: T.accentGold, transform: "rotate(45deg)" }} />
                <div style={{ flex: 1, height: 2, background: `linear-gradient(90deg, transparent, ${T.primary}40, transparent)` }} />
              </div>
            </div>
          </div>
        </div>
        <div className="t-wrapper">
          <div className="t-empty">
            <div className="t-empty-icon"><RiFileTextLine size={28} /></div>
            <p className="t-empty-title">No Guidelines Available</p>
            <p className="t-empty-sub">{error || "Presentation guidelines will be available soon."}</p>
            <button className="t-empty-btn" onClick={fetchGuidelines}>
              <RiRefreshLine size={14} style={{ marginRight: 6, verticalAlign: "middle" }} />
              Refresh
            </button>
            <Link to="/" className="t-empty-btn" style={{ marginTop: 8 }}>
              ← Back to Home
            </Link>
            {eventId && (
              <p style={{ fontSize: 11, color: T.textMuted, marginTop: 12 }}>
                Event ID: {eventId}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── No Items ─────────────────────────────────────────────────────
  if (!guidelines.guidelines || guidelines.guidelines.length === 0) {
    return (
      <div style={{ background: T.offWhite, minHeight: "100vh" }}>
        <style>{css}</style>
        <div className="t-hero">
          <div className="t-hero-i">
            <div>
              <div className="t-hero-lbl"><RiPresentationLine size={13} />Meeting21 · Presenter Information</div>
              <h1 className="t-hero-h">Presentation <em>Guidelines</em></h1>
              <p className="t-hero-p">{guidelines.description || "Essential information for all presenters"}</p>
            </div>
          </div>
        </div>
        <div className="t-wrapper">
          <div className="t-empty">
            <div className="t-empty-icon"><RiAddLine size={28} style={{ color: T.accentGold }} /></div>
            <p className="t-empty-title">Guidelines Coming Soon</p>
            <p className="t-empty-sub">Presentation guidelines are being prepared. Please check back later.</p>
            <Link to="/" className="t-empty-btn">← Back to Home</Link>
          </div>
        </div>
      </div>
    );
  }

  // ── Main Render ──────────────────────────────────────────────────
  const sortedGuidelines = [...guidelines.guidelines].sort((a, b) => a.order - b.order);
  const totalGuidelines = sortedGuidelines.length;

  return (
    <div style={{ background: T.offWhite, minHeight: "100vh" }}>
      <style>{css}</style>

      {/* ── HERO ──────────────────────────────────────────────────── */}
      <section className="t-hero">
        <div className="t-hero-i">
          <div>
            <div className="t-hero-lbl">
              <RiPresentationLine size={13} />
              Meeting21 · Presenter Information
            </div>
            <h1 className="t-hero-h">
              Presentation <em>Guidelines</em>
            </h1>
            <p className="t-hero-p">
              {guidelines.description ||
                "Essential information for all presenters at Meeting21. Please review these guidelines carefully to ensure a smooth and successful presentation experience."}
            </p>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 24 }}>
              <div style={{ flex: 1, height: 2, background: `linear-gradient(90deg, transparent, ${T.primary}40, transparent)` }} />
              <div style={{ width: 8, height: 8, background: T.accentGold, transform: "rotate(45deg)" }} />
              <div style={{ flex: 1, height: 2, background: `linear-gradient(90deg, transparent, ${T.primary}40, transparent)` }} />
            </div>
          </div>
          <div className="t-stats">
            <div className="t-st">
              <span className="t-st-n">{totalGuidelines}</span>
              <span className="t-st-l">Guidelines</span>
            </div>
            <div className="t-st">
              <span className="t-st-n">{guidelines.version || "1.0"}</span>
              <span className="t-st-l">Version</span>
            </div>
            <div className="t-st">
              <span className="t-st-n">{new Date().getFullYear()}</span>
              <span className="t-st-l">Edition</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── ACTION BUTTONS ────────────────────────────────────────── */}
      <div className="t-actions">
        <button
          onClick={() => setViewMode(viewMode === "list" ? "grid" : "list")}
          className="t-act-btn"
        >
          {viewMode === "list" ? "🔲 Grid View" : "☰ List View"}
        </button>
        <button onClick={handlePrint} className="t-act-btn">
          <RiPrinterLine size={16} /> Print Guidelines
        </button>
      </div>

      {/* ── CONTENT ───────────────────────────────────────────────── */}
      <div className="t-wrapper">
        <div className="t-container">

          {/* Section Header */}
          <div className="t-section-header">
            <h2 className="t-section-title">{guidelines.title || "Important Information for Presenters"}</h2>
            <div className="t-divider" />
            <p style={{ color: T.textMuted, fontSize: 15 }}>
              Please follow these guidelines carefully to ensure your presentation meets our standards
            </p>
          </div>

          {/* ── GRID VIEW ─────────────────────────────────────────── */}
          {viewMode === "grid" ? (
            <div className="t-grid">
              {sortedGuidelines.map((item, index) => (
                <motion.div
                  key={item._id || index}
                  className="t-grid-card"
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                >
                  <div className="t-grid-header">
                    <div className="t-grid-icon">{item.icon || "📌"}</div>
                    <span className="t-card-order">Guideline #{item.order + 1}</span>
                    <h3 className="t-card-title">{item.title}</h3>
                  </div>
                  <div className="t-grid-body">
                    <p className="t-card-desc">{item.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            /* ── LIST VIEW ────────────────────────────────────────── */
            <div className="t-layout">
              {/* Sidebar Navigation */}
              <div className="t-sidebar">
                <h3 className="t-sidebar-title">
                  <RiCheckLine size={16} style={{ color: T.primary }} />
                  Quick Navigation
                </h3>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {sortedGuidelines.map((item, index) => (
                    <button
                      key={index}
                      className={`t-nav-item ${expandedIndex === index ? "active" : ""}`}
                      onClick={() => {
                        const el = document.getElementById(`pg-${index}`);
                        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                        setExpandedIndex(index);
                      }}
                    >
                      <span style={{ fontSize: 16 }}>{item.icon || "📌"}</span>
                      <span style={{ flex: 1, fontSize: 13, textAlign: "left" }}>{item.title}</span>
                    </button>
                  ))}
                </div>

                {/* Last Updated */}
                <div style={{ marginTop: 20, paddingTop: 16, borderTop: `1px solid ${T.borderLight}` }}>
                  <p style={{ fontSize: 11, color: T.textMuted }}>
                    Last Updated:{" "}
                    {guidelines.updatedAt
                      ? new Date(guidelines.updatedAt).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })
                      : "N/A"}
                  </p>
                  <p style={{ fontSize: 11, color: T.textMuted, marginTop: 4 }}>
                    Version: {guidelines.version || "1.0"}
                  </p>
                </div>
              </div>

              {/* Main Cards */}
              <div>
                {sortedGuidelines.map((item, index) => (
                  <motion.div
                    key={item._id || index}
                    id={`pg-${index}`}
                    className="t-card"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.04 }}
                    onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
                  >
                    <div className="t-card-header">
                      <div className="t-card-icon">{item.icon || "📌"}</div>
                      <div style={{ flex: 1 }}>
                        <span className="t-card-order">#{item.order + 1}</span>
                        <h3 className="t-card-title">{item.title}</h3>
                        <p className="t-card-desc">
                          {expandedIndex === index
                            ? item.description
                            : `${item.description.substring(0, 130)}${item.description.length > 130 ? "…" : ""}`}
                        </p>
                      </div>
                      <RiArrowRightLine
                        className="t-expand-icon"
                        style={{
                          transform: expandedIndex === index ? "rotate(90deg)" : "none",
                          transition: "transform 0.3s",
                          color: T.primaryLight,
                          flexShrink: 0,
                        }}
                        size={20}
                      />
                    </div>

                    {expandedIndex === index && (
                      <motion.div
                        className="t-card-body"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        transition={{ duration: 0.3 }}
                      >
                        <p style={{ fontSize: 15, color: T.textMid, lineHeight: 1.75, marginBottom: 12 }}>
                          {item.description}
                        </p>
                        <button
                          className="t-copy"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigator.clipboard.writeText(item.description);
                            toast.success("Copied to clipboard!");
                          }}
                        >
                          📋 Copy Guideline
                        </button>
                      </motion.div>
                    )}
                  </motion.div>
                ))}

                {/* ✅ Contact Card with dynamic email and phone from API (per event) */}
                <div className="t-contact">
                  <h3 style={{ fontSize: 20, fontWeight: 700, color: T.white, marginBottom: 10 }}>
                    Need Help?
                  </h3>
                  <p style={{ color: `${T.white}90`, marginBottom: 20, fontSize: 14 }}>
                    If you have any questions about presentation guidelines, please contact us
                  </p>
                  <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 24 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, color: T.white, fontSize: 14 }}>
                      <RiMailLine size={18} /><span>{contactInfo.email}</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, color: T.white, fontSize: 14 }}>
                      <RiPhoneLine size={18} /><span>{contactInfo.phone}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── CTA SECTION ───────────────────────────────────────────── */}
      <div className="t-cta-wrap">
        <motion.div
          className="t-cta"
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          viewport={{ once: true }}
        >
          <div>
            <div className="t-cta-tag">Call for Abstracts</div>
            <h2 className="t-cta-h">Ready to Present at the Summit?</h2>
            <p className="t-cta-p">Submit your abstract today and secure your speaking slot.</p>
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 10 }}>
            <Link
              to={eventId ? `/register?eventId=${eventId}` : "/register"}
              className="t-cta-btn"
            >
              Register Now <RiArrowRightLine size={15} />
            </Link>
            <Link to={`/contact${eventId ? `?eventId=${eventId}` : ''}`} className="t-cta-btn-outline">
              Contact Us <RiArrowRightLine size={15} />
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PresentationGuidelinesPage;